import { createContext, useCallback, useMemo } from 'react';
import { UseQueryResult } from 'react-query';
import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';

import { PATHS } from '../../../../shared/constants';
import { useCurrentUser } from '../../../../shared/context';
import { Permission } from '../../../../shared/enum';
import { useContextFallback } from '../../../../shared/hooks';
import { Folder } from '../models';
import { useFoldersTreeQuery } from '../queries';
import { findFolderInTree } from '../utils';

type FolderTreeContextState = {
  selectedFolderPath: Folder[];
  isMainFolder: boolean;
  canCreateMainFolder: boolean;
  selectedFolder?: Folder;
  handleSelectFolder: (folderId: string) => void;
} & UseQueryResult<Folder[], unknown>;

const FolderTreeContext = createContext<FolderTreeContextState | undefined>(
  undefined
);
FolderTreeContext.displayName = 'FolderTreeContext';

export const FolderTreeProvider: React.FC = ({ children }) => {
  const { folderId } = useParams<{ folderId?: string }>();
  const foldersTreeQuery = useFoldersTreeQuery();
  const history = useHistory();
  const location = useLocation();
  const user = useCurrentUser();

  const [selectedFolder, selectedFolderPath] = useMemo(() => {
    if (!(folderId && foldersTreeQuery.data)) {
      return [undefined, []];
    }

    return findFolderInTree(folderId, foldersTreeQuery.data);
  }, [folderId, foldersTreeQuery]);

  const handleSelectFolder = useCallback(
    (id: string) => {
      const newPath = generatePath(PATHS.Documents, { folderId: id });
      if (location.pathname !== newPath) {
        history.push(newPath);
      }
    },
    [history, location.pathname]
  );

  const value = useMemo(
    () => ({
      ...foldersTreeQuery,
      selectedFolderPath,
      isMainFolder: selectedFolderPath.length === 0,
      canCreateMainFolder: user.permissions.includes(
        Permission.ManageMainFolders
      ),
      selectedFolder,
      handleSelectFolder,
    }),
    [
      foldersTreeQuery,
      handleSelectFolder,
      selectedFolder,
      selectedFolderPath,
      user.permissions,
    ]
  );

  return (
    <FolderTreeContext.Provider value={value}>
      {children}
    </FolderTreeContext.Provider>
  );
};

export const useFolderTree = () => useContextFallback(FolderTreeContext);
